'use client';

import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Button,
  Card,
  DatePicker,
  Flex,
  Select,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import type { OrganizationBareLogPreviewItem } from '@evimo/types/api/goldenRecord/export-bare-logs/export-bare-logs.response.dto';
import { useAuth } from '~/app/auth/context/AuthContext';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { JsonView } from '~/app/components/common/JsonView/JsonView';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { AssetLogsService } from '~/app/services/goldenRecord/asset-logs.service';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;

function formatPreview(value: unknown): string {
  if (value == null) return '—';
  const text =
    typeof value === 'string' ? value : JSON.stringify(value, null, 0);
  return text.length > 120 ? `${text.slice(0, 120)}…` : text;
}

function BareLogExpandedContent({ value }: { value: unknown }) {
  return (
    <div style={{ maxHeight: 360, overflow: 'auto', padding: '4px 8px' }}>
      <JsonView value={value} wrapInCard={false} maxHeight={320} />
    </div>
  );
}

export function LogsExportPageContainer() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const { t } = useTranslation('logsExport');
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const [organizationId, setOrganizationId] = useState('');
  const [page, setPage] = useState(1);
  const [previewEnabled, setPreviewEnabled] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<
    [Dayjs | null, Dayjs | null] | null
  >([dayjs().subtract(1, 'month'), dayjs()]);
  const [isDownloading, setIsDownloading] = useState(false);

  const from = dateRange?.[0]?.toISOString();
  const to = dateRange?.[1]?.toISOString();

  const {
    data: organizations = [],
    isLoading: isLoadingOrganizations,
    error: organizationsError,
  } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => OrganizationService.findAllOrganizations(),
    enabled: isSuperAdmin && !isAuthLoading,
  });

  const {
    data: previewData,
    isFetching: isLoadingPreview,
    error: previewError,
  } = useQuery({
    queryKey: ['organization-bare-logs', organizationId, from, to, page],
    queryFn: () =>
      AssetLogsService.previewOrganizationBareLogs(organizationId, {
        from: from!,
        to: to!,
        page,
        limit: PAGE_SIZE,
      }),
    enabled:
      previewEnabled && !!organizationId && !!from && !!to && isSuperAdmin,
  });

  useEffect(() => {
    if (isSuperAdmin && organizations.length > 0 && !organizationId) {
      setOrganizationId(organizations[0].id);
    }
  }, [isSuperAdmin, organizations, organizationId]);

  const columns: ColumnsType<OrganizationBareLogPreviewItem> = useMemo(
    () => [
      {
        title: t('table.serialNumber'),
        dataIndex: 'serialNumber',
        key: 'serialNumber',
        width: 160,
        ellipsis: true,
      },
      {
        title: t('table.messageType'),
        dataIndex: 'messageType',
        key: 'messageType',
        width: 140,
        ellipsis: true,
      },
      {
        title: t('table.logId'),
        dataIndex: 'logId',
        key: 'logId',
        width: 360,
        ellipsis: false,
        render: (logId: string) => (
          <Text style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
            {logId}
          </Text>
        ),
      },
      {
        title: t('table.receivedAt'),
        key: 'receivedAt',
        width: 180,
        render: (_, record) =>
          dayjs(record.receivedAt ?? record.createdAt).format(
            'YYYY-MM-DD HH:mm:ss',
          ),
      },
      {
        title: t('table.rawMessage'),
        key: 'rawMessage',
        ellipsis: true,
        render: (_, record) => (
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>
            {formatPreview(record.rawMessage)}
          </Text>
        ),
      },
    ],
    [t],
  );

  if (isAuthLoading) {
    return <Loader />;
  }

  if (!isSuperAdmin) {
    return <Navigate to="/home" replace />;
  }

  if (organizationsError) {
    return (
      <ErrorBox
        title={t('errors.loadOrganizations')}
        description={organizationsError.message}
      />
    );
  }

  const handlePreview = () => {
    setPage(1);
    setExpandedRowKeys([]);
    setPreviewEnabled(true);
  };

  const handleDownload = async () => {
    if (!organizationId || !from || !to) {
      return;
    }

    setIsDownloading(true);
    try {
      await AssetLogsService.downloadOrganizationBareLogs(organizationId, {
        from,
        to,
      });
      message.success(t('messages.downloadSuccess'));
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : t('messages.downloadError'),
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Flex vertical gap="large" className="app-page app-detail-container">
      <Flex vertical gap={8}>
        <Title level={3} style={{ margin: 0 }}>
          {t('title')}
        </Title>
        <Text type="secondary">{t('description')}</Text>
      </Flex>

      <Card style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}>
        <Flex vertical gap={16}>
          <Flex vertical gap={8}>
            <Text strong>{t('fields.organization')}</Text>
            <Select
              showSearch
              optionFilterProp="label"
              loading={isLoadingOrganizations}
              value={organizationId || undefined}
              onChange={(value) => {
                setOrganizationId(value);
                setPreviewEnabled(false);
                setPage(1);
                setExpandedRowKeys([]);
              }}
              options={organizations.map((organization) => ({
                value: organization.id,
                label: organization.name,
              }))}
              placeholder={t('fields.organizationPlaceholder')}
              style={{ maxWidth: 480 }}
            />
          </Flex>

          <Flex vertical gap={8}>
            <Text strong>{t('fields.dateRange')}</Text>
            <DatePicker.RangePicker
              showTime
              format="YYYY-MM-DD HH:mm"
              value={dateRange}
              onChange={(dates) => {
                setDateRange(dates as [Dayjs, Dayjs]);
                setPreviewEnabled(false);
                setPage(1);
                setExpandedRowKeys([]);
              }}
              style={{ maxWidth: 480 }}
            />
          </Flex>

          <Flex gap={8} wrap="wrap">
            <Button
              icon={<EyeOutlined />}
              disabled={!organizationId || !from || !to}
              onClick={handlePreview}
            >
              {t('actions.preview')}
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              disabled={!organizationId || !from || !to}
              onClick={handleDownload}
            >
              {t('actions.downloadZip')}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {previewEnabled ? (
        <Card style={{ borderRadius: 16, border: '1px solid #e2e8f0' }}>
          {previewError ? (
            <Text type="danger">
              {previewError instanceof Error
                ? previewError.message
                : t('messages.previewError')}
            </Text>
          ) : (
            <EvimoTable<OrganizationBareLogPreviewItem>
              rowKey={(record) => `${record.serialNumber}-${record.logId}`}
              loading={isLoadingPreview}
              columns={columns}
              dataSource={previewData?.items ?? []}
              scroll={{ x: 1240 }}
              expandable={{
                expandedRowKeys,
                onExpandedRowsChange: (keys) =>
                  setExpandedRowKeys(keys as string[]),
                expandedRowRender: (record) => (
                  <BareLogExpandedContent value={record.rawMessage} />
                ),
                rowExpandable: (record) => record.rawMessage != null,
              }}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: previewData?.total ?? 0,
                showSizeChanger: false,
                onChange: (nextPage) => {
                  setPage(nextPage);
                  setExpandedRowKeys([]);
                },
              }}
              locale={{ emptyText: t('table.empty') }}
            />
          )}
        </Card>
      ) : null}
    </Flex>
  );
}
